<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import type { EventImportResult, MassImportOptions } from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { useAppToast } from '@/composables/useAppToast'
import { eventService } from '@/services/eventService'
import { EventImportState } from '@/types'
import { getApiErrorMessage } from '@/utils/apiError'

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useI18n()
const toast = useAppToast()
const route = useRoute()
const { selectedSemester } = useMetadata()
const { enableSemesterSelector, setBreadcrumbs } = useBreadcrumbs()

const workspaceId = computed(() => Number((route.params as { id?: string }).id))

const selectedFile = ref<File | null>(null)
const importing = ref(false)
const results = ref<EventImportResult[] | null>(null)
const uploadError = ref<string | null>(null)

const lecturerMode = ref<MassImportOptions['lecturerMode']>('none')
const campusContactMode = ref<MassImportOptions['campusContactMode']>('none')

const lecturerModeOptions = computed(() => [
  { label: t('pages.manage-events-import.lecturerModeNone'), value: 'none' as const },
  { label: t('pages.manage-events-import.lecturerModeReadonly'), value: 'readonly' as const },
  { label: t('pages.manage-events-import.lecturerModeFull'), value: 'full' as const },
])

const campusContactModeOptions = computed(() => [
  { label: t('pages.manage-events-import.campusContactModeNone'), value: 'none' as const },
  { label: t('pages.manage-events-import.campusContactModeEmail'), value: 'email' as const },
  { label: t('pages.manage-events-import.campusContactModePerson'), value: 'person' as const },
  { label: t('pages.manage-events-import.campusContactModeBoth'), value: 'both' as const },
])

const onFileSelect = (event: { files: File[] }) => {
  selectedFile.value = event.files?.[0] ?? null
  results.value = null
  uploadError.value = null
}

const onFileClear = () => {
  selectedFile.value = null
  results.value = null
  uploadError.value = null
}

const handleImport = async () => {
  if (!selectedFile.value || !selectedSemester.value) return

  importing.value = true
  uploadError.value = null
  results.value = null

  try {
    const data = await eventService.massImport(
      selectedSemester.value.id,
      selectedFile.value,
      {
        lecturerMode: lecturerMode.value,
        campusContactMode: campusContactMode.value,
      },
      workspaceId.value,
    )
    results.value = data

    const created = data.filter((r) => r.importResultState === EventImportState.Created).length
    const updated = data.filter((r) => r.importResultState === EventImportState.Updated).length
    const noChange = data.filter((r) => r.importResultState === EventImportState.NoChange).length
    const failed = data.filter((r) => r.importResultState === EventImportState.Failed).length

    toast.add({
      color: failed > 0 ? 'warning' : 'success',
      title: t('pages.manage-events-import.resultTitle'),
      description: t('pages.manage-events-import.resultSummary', {
        created,
        updated,
        noChange,
        failed,
      }),
      duration: 5000,
    })
  } catch (error) {
    uploadError.value = getApiErrorMessage(error, t, t('pages.manage-events-import.errorOccurred'))
  } finally {
    importing.value = false
  }
}

const resultStateSeverity = (state: EventImportState) => {
  switch (state) {
    case EventImportState.Created:
      return 'success'
    case EventImportState.Updated:
      return 'info'
    case EventImportState.NoChange:
      return 'secondary'
    case EventImportState.Failed:
      return 'danger'
    case EventImportState.Skipped:
      return 'secondary'
    default:
      return 'secondary'
  }
}

const resultStateLabel = (state: EventImportState) => {
  switch (state) {
    case EventImportState.Created:
      return t('pages.manage-events-import.resultCreated')
    case EventImportState.Updated:
      return t('pages.manage-events-import.resultUpdated')
    case EventImportState.NoChange:
      return t('pages.manage-events-import.resultNoChange')
    case EventImportState.Failed:
      return t('pages.manage-events-import.resultFailed')
    case EventImportState.Skipped:
      return t('pages.manage-events-import.resultSkipped')
    default:
      return ''
  }
}

const filteredResults = computed(() => {
  if (!results.value) return []
  return results.value.filter((r) => r.importResultState !== EventImportState.Skipped)
})
const resultColumns = computed<TableColumn<EventImportResult>[]>(() => [
  { accessorKey: 'row', header: t('pages.manage-events-import.line') },
  { id: 'name', header: t('fields.event.nameDE') },
  { id: 'status', header: t('pages.manage-events-import.status') },
  { id: 'errors', header: t('pages.manage-events-import.errors') },
])

onMounted(() => {
  enableSemesterSelector()
  setBreadcrumbs([
    { label: t('router.workspaces'), route: '/workspaces' },
    { label: t('router.workspaces-id-events'), route: `/workspaces/${workspaceId.value}/events` },
    { label: t('pages.manage-events-import.title') },
  ])
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Upload Section -->
    <UCard>
      <template #header>{{ t('pages.manage-events-import.title') }}</template>
      <template #subtitle>{{ t('pages.manage-events-import.intro') }}</template>
      <template #default>
        <div class="flex flex-col gap-4">
          <AppFileUpload
            mode="basic"
            accept=".xlsx"
            :max-file-size="10485760"
            :auto="false"
            :choose-label="t('pages.manage-events-import.chooseFile')"
            @select="onFileSelect"
            @clear="onFileClear"
          />
          <small class="text-muted">{{ t('pages.manage-events-import.fileHint') }}</small>

          <!-- Import Options -->
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">{{
                t('pages.manage-events-import.lecturerMode')
              }}</label>
              <AppSelect
                v-model="lecturerMode"
                :options="lecturerModeOptions"
                option-label="label"
                option-value="value"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">{{
                t('pages.manage-events-import.campusContactMode')
              }}</label>
              <AppSelect
                v-model="campusContactMode"
                :options="campusContactModeOptions"
                option-label="label"
                option-value="value"
              />
              <small class="text-muted">{{
                t('pages.manage-events-import.campusContactModeHint')
              }}</small>
            </div>
          </div>

          <div>
            <UButton
              :label="t('pages.manage-events-import.importButton')"
              icon="i-lucide-upload"
              :loading="importing"
              :disabled="!selectedFile || !selectedSemester || importing"
              @click="handleImport"
            />
          </div>

          <!-- Error Banner -->
          <AppAlert v-if="uploadError" color="error" :closable="false">
            {{ uploadError }}
          </AppAlert>
        </div>
      </template>
    </UCard>

    <!-- Results Section -->
    <UCard v-if="filteredResults.length > 0">
      <template #header>{{ t('pages.manage-events-import.resultTitle') }}</template>
      <template #default>
        <AppDataTableShell :data="filteredResults" :columns="resultColumns">
          <template #name-cell="{ row }">
            <router-link
              v-if="row.original.id"
              :to="`/manage/events/${row.original.id}/overview`"
              class="text-[var(--app-text-primary)] hover:underline"
            >
              {{ row.original.nameDE || '—' }}
            </router-link>
            <span v-else>{{ row.original.nameDE || '—' }}</span>
          </template>
          <template #status-cell="{ row }">
            <UBadge
              :label="resultStateLabel(row.original.importResultState)"
              :color="toAppColor(resultStateSeverity(row.original.importResultState))"
            />
          </template>
          <template #errors-cell="{ row }">
            <ul
              v-if="row.original.importErrors?.length"
              class="m-0 list-disc pl-4 text-sm text-red-700 dark:text-red-400"
            >
              <li v-for="(error, idx) in row.original.importErrors" :key="idx">{{ error }}</li>
            </ul>
            <ul
              v-if="row.original.importWarnings?.length"
              class="m-0 list-disc pl-4 text-sm text-amber-700 dark:text-amber-400"
            >
              <li v-for="(warning, idx) in row.original.importWarnings" :key="idx">
                {{ warning }}
              </li>
            </ul>
          </template>
        </AppDataTableShell>
      </template>
    </UCard>
  </div>
</template>
